/**
 * The plan's Changes tab (RP-25, approved 2026-10-17).
 *
 * One entry per save since the plan was sent — who saved it, in which role, when, what
 * the plan was at the time, and the major changes it made, each before → after. Status
 * moves, approvals and version commits sit in the same list as milestones. The user who
 * saved a change can give its reason here, afterwards, whatever screen they saved it from.
 *
 * Calm first view: entries are collapsed to one line; the before → after detail and the
 * filters open on demand.
 */

import { ChevronDown, ChevronRight, Filter, History } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/ui/status-badge";
import { Textarea } from "@/components/ui/textarea";
import {
  type PlanChangeFilters,
  usePlanChanges,
  useSetPlanChangeReason,
} from "@/hooks/use-resource-plans";
import { NYMBL_ROLES } from "@/lib/nymbl-roles";
import type { FieldChange, PlanChangeItem } from "@/lib/plan-change-diff";
import { formatPlanHours, formatPlanMoney } from "@/lib/plan-format";
import { planStatusLabel } from "@/lib/plan-status-labels";
import type { PlanChangeEventView } from "@/schemas/plan-changes";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

const ALL = "all";

// ── Formatting ────────────────────────────────────────────────────────────────

/** "2026-02-01" or "2026-02" → "Feb 2026". */
function monthLabel(month: string): string {
  const d = new Date(`${month.slice(0, 7)}-01T00:00:00Z`);
  return d.toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}

function hours(value: string | null): string {
  return value === null ? "none" : `${formatPlanHours(value)} h`;
}

function fieldValue(change: FieldChange, value: string | null): string {
  if (value === null || value === "") return "none";
  if (change.field === "Rate" || change.field === "Contracted Fee" || change.field === "Discount") {
    return formatPlanMoney(value);
  }
  if (change.field === "Start month" || change.field === "End month") return monthLabel(value);
  return value;
}

function fields(list: readonly FieldChange[]): string {
  return list
    .map((f) => `${f.field} ${fieldValue(f, f.before)} → ${fieldValue(f, f.after)}`)
    .join("; ");
}

function resourceList(list: readonly string[], confirmed: boolean): string {
  if (list.length === 0) return "nobody";
  return `${list.join(", ")}${confirmed && list.length === 1 ? " (confirmed)" : ""}`;
}

function span(first: string | null, last: string | null): string {
  if (first === null || last === null) return "";
  return first === last ? `, ${monthLabel(first)}` : `, ${monthLabel(first)}–${monthLabel(last)}`;
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── One item's before → after ─────────────────────────────────────────────────

function ItemDetail({ item }: { item: PlanChangeItem }) {
  switch (item.type) {
    case "line_added":
      return (
        <p>
          <span className="font-medium">Added {item.label}</span> —{" "}
          {item.after.runRate !== null
            ? `${formatPlanHours(item.after.runRate)} h/mo`
            : `${formatPlanHours(item.after.totalHours)} h${span(item.after.firstMonth, item.after.lastMonth)}`}
          {item.after.resources.length > 0 && ` · ${item.after.resources.join(", ")}`}
        </p>
      );
    case "line_removed":
      return (
        <p>
          <span className="font-medium">Removed {item.label}</span> —{" "}
          {item.before.runRate !== null
            ? `${formatPlanHours(item.before.runRate)} h/mo`
            : `${formatPlanHours(item.before.totalHours)} h${span(item.before.firstMonth, item.before.lastMonth)}`}
          {item.before.resources.length > 0 && ` · ${item.before.resources.join(", ")}`}
        </p>
      );
    case "line_hours":
      return (
        <div>
          <p>
            <span className="font-medium">{item.label}</span> —{" "}
            {item.before.runRate !== null || item.after.runRate !== null
              ? `${hours(item.before.runRate)} → ${hours(item.after.runRate)} per month`
              : `${hours(item.before.totalHours)} → ${hours(item.after.totalHours)}`}
          </p>
          {item.months.length > 0 && (
            <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {item.months.map((m) => (
                <li key={m.month}>
                  {monthLabel(m.month)}: {hours(m.before)} → {hours(m.after)}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    case "line_details":
      return (
        <p>
          <span className="font-medium">{item.label}</span> — {fields(item.fields)}
        </p>
      );
    case "resource":
      return (
        <p>
          <span className="font-medium">{item.label}</span> —{" "}
          {resourceList(item.before.resources, item.before.confirmed)} →{" "}
          {resourceList(item.after.resources, item.after.confirmed)}
        </p>
      );
    case "terms":
      return (
        <p>
          <span className="font-medium">Terms</span> — {fields(item.fields)}
        </p>
      );
    case "milestone":
      return null;
  }
}

// ── The reason, given by whoever saved the change ─────────────────────────────

function ReasonEditor({ event, planId }: { event: PlanChangeEventView; planId: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(event.reason ?? "");
  const setReason = useSetPlanChangeReason(planId);

  if (!editing) {
    return (
      <Button
        size="sm"
        variant="link"
        className="h-auto p-0 text-xs"
        onClick={() => {
          setDraft(event.reason ?? "");
          setEditing(true);
        }}
      >
        {event.reason === null ? "Add a reason" : "Edit reason"}
      </Button>
    );
  }
  return (
    <div className="mt-2 flex flex-col gap-2">
      <Textarea
        value={draft}
        maxLength={500}
        rows={2}
        placeholder="Why was this changed?"
        aria-label="Reason for this change"
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={setReason.isPending}
          onClick={() =>
            setReason.mutate(
              { eventId: event.id, reason: draft.trim() === "" ? null : draft.trim() },
              { onSuccess: () => setEditing(false) },
            )
          }
        >
          Save reason
        </Button>
        <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── One entry ─────────────────────────────────────────────────────────────────

function ChangeEntry({ event, planId }: { event: PlanChangeEventView; planId: string }) {
  const [open, setOpen] = useState(false);
  const details = event.items.filter((i) => i.type !== "milestone");
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <li className="flex gap-3 border-t border-border py-3 first:border-t-0">
      <span
        aria-hidden
        className={
          event.kind === "milestone"
            ? "mt-1.5 size-2 shrink-0 rounded-full bg-primary"
            : "mt-1.5 size-2 shrink-0 rounded-full bg-foreground/25"
        }
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {details.length > 0 ? (
            <button
              type="button"
              className="flex items-center gap-1 text-left text-sm font-medium hover:underline"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              <Chevron className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              {event.summary}
            </button>
          ) : (
            <span className="text-sm font-medium">{event.summary}</span>
          )}
          {event.commercial && (
            <StatusBadge status="pending" title="Price, hours or dates changed">
              Commercial change
            </StatusBadge>
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {event.actorName ?? "Unknown user"} · {event.actorRole} · {when(event.createdAt)} · while{" "}
          {planStatusLabel(event.planStatus)}
        </p>
        {open && details.length > 0 && (
          <div className="mt-2 flex flex-col gap-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
            {details.map((item) => (
              // One item per type per line within a save, and one terms item.
              <ItemDetail
                key={`${item.type}:${"planLineId" in item ? item.planLineId : ""}`}
                item={item}
              />
            ))}
          </div>
        )}
        {event.reason !== null && (
          <p className="mt-1 text-sm text-muted-foreground">Reason: {event.reason}</p>
        )}
        {event.canEditReason && event.kind === "edit" && (
          <ReasonEditor event={event} planId={planId} />
        )}
      </div>
    </li>
  );
}

// ── Filters ───────────────────────────────────────────────────────────────────

function lineOptions(plan: ResourcePlanDetailView): Array<{ id: string; label: string }> {
  const workstreams = new Map(plan.workstreams.map((w) => [w.id, w.name]));
  return plan.lines.map((l) => {
    const client = l.clientLaborCategory ? ` (${l.clientLaborCategory})` : "";
    const ws = l.workstreamId === null ? undefined : workstreams.get(l.workstreamId);
    return { id: l.id, label: `${l.laborCategoryName}${client}${ws ? ` · ${ws}` : ""}` };
  });
}

function ChangeFilters({
  plan,
  filters,
  onChange,
}: {
  plan: ResourcePlanDetailView;
  filters: PlanChangeFilters;
  onChange: (next: PlanChangeFilters) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-card px-3 py-3">
      <div className="flex flex-col gap-1">
        <Label htmlFor="changes-role" className="text-xs">
          Changed by
        </Label>
        <Select
          value={filters.role ?? ALL}
          onValueChange={(v) => onChange({ ...filters, role: v === ALL ? undefined : v })}
        >
          <SelectTrigger id="changes-role" className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any role</SelectItem>
            {NYMBL_ROLES.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="changes-line" className="text-xs">
          Plan Line
        </Label>
        <Select
          value={filters.planLineId ?? ALL}
          onValueChange={(v) => onChange({ ...filters, planLineId: v === ALL ? undefined : v })}
        >
          <SelectTrigger id="changes-line" className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any line</SelectItem>
            {lineOptions(plan).map((l) => (
              <SelectItem key={l.id} value={l.id}>
                {l.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="changes-from" className="text-xs">
          From
        </Label>
        <Input
          id="changes-from"
          type="date"
          className="w-40"
          value={filters.from ?? ""}
          onChange={(e) => onChange({ ...filters, from: e.target.value || undefined })}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="changes-to" className="text-xs">
          To
        </Label>
        <Input
          id="changes-to"
          type="date"
          className="w-40"
          value={filters.to ?? ""}
          onChange={(e) => onChange({ ...filters, to: e.target.value || undefined })}
        />
      </div>
      <div className="flex h-9 items-center gap-2">
        <Checkbox
          id="changes-commercial"
          checked={filters.commercial === true}
          onCheckedChange={(v) => onChange({ ...filters, commercial: v === true || undefined })}
        />
        <Label htmlFor="changes-commercial" className="text-sm">
          Commercial changes only
        </Label>
      </div>
      <Button size="sm" variant="ghost" onClick={() => onChange({})}>
        Clear
      </Button>
    </div>
  );
}

// ── The tab ───────────────────────────────────────────────────────────────────

export function PlanChangesPanel({ plan }: { plan: ResourcePlanDetailView }) {
  const [filters, setFilters] = useState<PlanChangeFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const query = usePlanChanges(plan.id, filters);
  const events = query.data?.pages.flatMap((p) => p.data) ?? [];
  const filtered = Object.values(filters).some((v) => v !== undefined);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {plan.status === "Draft"
            ? "Submissions to Resourcing and rejections. Edits are recorded once the plan is sent."
            : "Every save since this plan was sent — major changes only, before → after."}
        </p>
        <Button
          size="sm"
          variant="outline"
          aria-expanded={showFilters}
          onClick={() => setShowFilters((v) => !v)}
        >
          <Filter className="size-4" aria-hidden />
          Filters
        </Button>
      </div>
      {showFilters && <ChangeFilters plan={plan} filters={filters} onChange={setFilters} />}

      {query.isLoading ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</p>
      ) : query.isError ? (
        <ErrorState
          message={query.error instanceof Error ? query.error.message : "Could not load changes."}
          onRetry={() => void query.refetch()}
        />
      ) : events.length === 0 ? (
        <EmptyState
          icon={History}
          title={filtered ? "No changes match these filters" : "No changes yet"}
          description={
            filtered
              ? "Try a wider date range or another role."
              : plan.status === "Draft"
                ? "Edits are recorded once the plan is sent. This plan is still Open."
                : "Changes saved from here on will be listed, newest first."
          }
        />
      ) : (
        <>
          <ul className="rounded-md border border-border bg-card px-4">
            {events.map((event) => (
              <ChangeEntry key={event.id} event={event} planId={plan.id} />
            ))}
          </ul>
          {query.hasNextPage && (
            <Button
              size="sm"
              variant="outline"
              className="self-center"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {query.isFetchingNextPage ? "Loading…" : "Show older changes"}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
